import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { playStoreUrl, trackEvent } from './analytics';
import Seo from './Seo';
import Breadcrumbs, { buildBreadcrumbSchema } from './Breadcrumbs';
import './App.css';
import './Download.css';
import Footer from './Footer';
import Navbar from './Navbar';

const breadcrumbItems = [
  { name: 'Home', path: '/' },
  { name: 'Download', path: '/download' },
];

// Keep ratingValue/ratingCount in sync with the numbers shown on this page
// (dl-badge-n below) — Google requires structured-data ratings to match
// what's visibly displayed, or the rich result can be suppressed/penalized.
const downloadJsonLd = [
  buildBreadcrumbSchema(breadcrumbItems),
  {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: 'Baatcheet',
    description: 'Baatcheet is a voice-first social app for India — mood-matched audio calls, live rooms, and voice notes with KYC-verified, anonymous-first users.',
    operatingSystem: 'Android',
    applicationCategory: 'SocialNetworkingApplication',
    image: 'https://baatcheetapp.in/download-hero.webp',
    url: 'https://baatcheetapp.in/download',
    offers: {
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'INR',
    },
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: '4.4',
      ratingCount: '2000',
    },
  },
];

function Download() {
  useEffect(() => {
    const io = new IntersectionObserver(
      entries => entries.forEach(e => {
        if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target); }
      }),
      { threshold: 0.1 }
    );
    document.querySelectorAll('.reveal').forEach(el => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <>
      <Seo
        title="Download Baatcheet — Free Voice Calling App for Android | Baatcheet"
        description="Get Baatcheet free on Google Play. KYC-verified partners, crystal-clear HD audio, and instant matching in under 8 seconds."
        path="/download"
        jsonLd={downloadJsonLd}
      />
      <div className="bg" />
      <div className="confetti">
        <i className="dot s1" /><i className="cbar s2" /><i className="dot s3" /><i className="cbar s4" />
        <i className="dot s5" /><i className="dot s6" /><i className="cbar s7" /><i className="dot s8" />
        <i className="cbar s9" /><i className="dot s10" />
      </div>

      {/* ── Header ── */}
      <Navbar />
      <Breadcrumbs items={breadcrumbItems} />

      <main>

        {/* ── HERO ── */}
        <section className="dl-hero shell">
          <div className="dl-hero-grid">

            {/* Left copy */}
            <div className="dl-hero-copy reveal">
              <span className="dl-eyebrow">
                <span className="dl-eyebrow-dot" />
                Available on Android &amp; iOS
              </span>
              <h1 className="dl-title">
                Ab Baat<br />
                <span className="dl-title-hi">करो</span>{' '}
                <span className="dl-title-stamp">Khulke!</span>
              </h1>
              <p className="dl-sub">
                Real conversations. Verified partners. Zero fake profiles.
                Baatcheet is where talking is safe, fun, and genuinely enjoyable.
              </p>

              <div className="dl-store-row">
                <a
                  className="dl-store-btn dl-store-android"
                  href={playStoreUrl('download_page')}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => trackEvent('play_store_click', { location: 'download_page' })}
                >
                  <svg width="22" height="24" viewBox="0 0 512 512" aria-hidden="true">
                    <path fill="#4285F4" d="M48,59.49v393a4.33,4.33,0,0,0,7.37,3.07L260,256,55.37,56.42A4.33,4.33,0,0,0,48,59.49Z"/>
                    <path fill="#34A853" d="M345.8,174,89.17,32.64l-.07,0c-6.81-3.64-13.36.55-13.36,7.71L260,256Z"/>
                    <path fill="#EA4335" d="M89.1,479.36c0,7.16,6.55,11.35,13.36,7.71L345.8,338,260,256Z"/>
                    <path fill="#FBBC04" d="M447.34,230.13l-82.08-45.79L260,256l105.26,71.66,82.08-45.79a20.41,20.41,0,0,0,0-51.74Z"/>
                  </svg>
                  <div className="dl-store-text">
                    <span className="dl-store-top">GET IT ON</span>
                    <span className="dl-store-bot">Google Play</span>
                  </div>
                </a>
                <a className="dl-store-btn dl-store-ios" href="#">
                  <svg width="17" height="20" viewBox="0 0 814 1000" fill="var(--ink)" aria-hidden="true">
                    <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-57.8-155.5-127.4C46 790.7 0 663 0 541.8c0-194.3 126.4-297.5 250.8-297.5 66.1 0 121.2 43.4 162.7 43.4 39.5 0 101.1-46 176.3-46 28.5 0 130.9 2.6 198.3 99.2zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z"/>
                  </svg>
                  <div className="dl-store-text">
                    <span className="dl-store-top">DOWNLOAD ON</span>
                    <span className="dl-store-bot">App Store</span>
                  </div>
                </a>
              </div>

              <div className="dl-badges">
                <div className="dl-badge">
                  <span className="dl-badge-n">4.4★</span>
                  <span className="dl-badge-l">Rating</span>
                </div>
                <div className="dl-badge-sep" />
                <div className="dl-badge">
                  <span className="dl-badge-n">2K+</span>
                  <span className="dl-badge-l">Reviews</span>
                </div>
                <div className="dl-badge-sep" />
                <div className="dl-badge">
                  <span className="dl-badge-n">1M+</span>
                  <span className="dl-badge-l">Users</span>
                </div>
                <div className="dl-badge-sep" />
                <div className="dl-badge">
                  <span className="dl-badge-n">Free</span>
                  <span className="dl-badge-l">Always</span>
                </div>
              </div>
            </div>

            {/* Right: hero image */}
            <div className="dl-hero-visual reveal">
              <img src="/download-hero.webp" alt="Download Baatcheet voice calling app" className="dl-hero-img" width="1448" height="1086" fetchpriority="high" />
            </div>
          </div>
        </section>

        {/* ── WHY BAATCHEET ── */}
        <section className="dl-why shell">
          <div className="dl-section-head reveal">
            <span className="dl-section-tag">✦ What You Get</span>
            <h2 className="dl-section-title">One App,<br /><span className="dl-title-stamp-sm">Endless Benefits</span></h2>
          </div>
          <div className="dl-cards reveal">
            <div className="dl-card dl-card--cyan">
              <div className="dl-card-icon">🛡️</div>
              <h3>Verified Partners</h3>
              <p>Every user is KYC verified. No fake IDs, no catfishing — only real people, real conversations. <Link to="/safety">See our safety promise</Link>.</p>
              <div className="dl-card-tag">100% Real</div>
            </div>
            <div className="dl-card dl-card--gold">
              <div className="dl-card-icon">🎙️</div>
              <h3>Crystal Clear Audio</h3>
              <p>HD voice technology that makes every call feel like you're in the same room together.</p>
              <div className="dl-card-tag">HD Quality</div>
            </div>
            <div className="dl-card dl-card--rose">
              <div className="dl-card-icon">🔒</div>
              <h3>Private &amp; Secure</h3>
              <p>End-to-end encrypted calls. Your conversations stay yours — completely private, always.</p>
              <div className="dl-card-tag">E2E Encrypted</div>
            </div>
            <div className="dl-card dl-card--teal">
              <div className="dl-card-icon">⚡</div>
              <h3>Instant Match</h3>
              <p>Set your mood and find a partner in under 8 seconds. No waiting, no swiping endlessly.</p>
              <div className="dl-card-tag">Avg. 8 Seconds</div>
            </div>
          </div>
        </section>

        {/* ── REVIEWS ── */}
        <section className="dl-reviews shell">
          <div className="dl-section-head reveal">
            <span className="dl-section-tag">★ What People Are Saying</span>
            <h2 className="dl-section-title">India's<br /><span className="dl-title-stamp-sm">Favorite App</span></h2>
          </div>

          <div className="dl-testi-grid reveal">
            <div className="dl-testi">
              <div className="dl-stars">★★★★★</div>
              <p className="dl-testi-text">"Pehli baar kisi app par itna safe feel kiya. KYC verified hai toh fake profiles ki tension nahi."</p>
              <div className="dl-testi-user">
                <img src="/testi-1.webp" alt="Priya S., Baatcheet user from Mumbai" width="1024" height="1536" loading="lazy" decoding="async" />
                <div>
                  <div className="dl-testi-name">Priya S.</div>
                  <div className="dl-testi-loc">Mumbai, Maharashtra</div>
                </div>
              </div>
            </div>
            <div className="dl-testi dl-testi--featured">
              <div className="dl-testi-badge">Top Review</div>
              <div className="dl-stars">★★★★★</div>
              <p className="dl-testi-text">"Audio quality ekdum crystal clear hai. Jaise saamne baith ke baat ho rahi ho. Absolutely love it!"</p>
              <div className="dl-testi-user">
                <img src="/testi-2.webp" alt="Rahul M., Baatcheet user from Delhi" width="1254" height="1254" loading="lazy" decoding="async" />
                <div>
                  <div className="dl-testi-name">Rahul M.</div>
                  <div className="dl-testi-loc">Delhi, NCR</div>
                </div>
              </div>
            </div>
            <div className="dl-testi">
              <div className="dl-stars">★★★★★</div>
              <p className="dl-testi-text">"Finally found my people! Real conversations without the drama of other apps. Ek hi try mein hook ho gayi."</p>
              <div className="dl-testi-user">
                <img src="/testi-3.webp" alt="Ananya K., Baatcheet user from Bengaluru" width="1254" height="1254" loading="lazy" decoding="async" />
                <div>
                  <div className="dl-testi-name">Ananya K.</div>
                  <div className="dl-testi-loc">Bengaluru, Karnataka</div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── STEPS ── */}
        <section className="dl-steps shell">
          <div className="dl-steps-inner reveal">
            <div className="dl-steps-copy">
              <span className="dl-section-tag">↓ How to Get Started</span>
              <h2 className="dl-section-title">Three Steps,<br /><span className="dl-title-stamp-sm">You're All Set</span><br />&amp; Ready!</h2>
              <p className="dl-steps-sub">From download to your first call — everything in under 2 minutes. Promise. Full walkthrough <Link to="/how-it-works">here</Link>.</p>
            </div>
            <div className="dl-steps-list">
              <div className="dl-step">
                <div className="dl-step-num">01</div>
                <div className="dl-step-body">
                  <h3>Download the App</h3>
                  <p>Get it free from the Play Store or App Store. No hidden charges, ever.</p>
                </div>
              </div>
              <div className="dl-step">
                <div className="dl-step-num">02</div>
                <div className="dl-step-body">
                  <h3>Create Your Profile</h3>
                  <p>Quick signup — set your vibe, pick your interests, and complete KYC verification.</p>
                </div>
              </div>
              <div className="dl-step">
                <div className="dl-step-num">03</div>
                <div className="dl-step-body">
                  <h3>Start Talking!</h3>
                  <p>Get matched and connect instantly — your very first call is always free.</p>
                </div>
              </div>
            </div>
          </div>
        </section>


      </main>

      <Footer />
    </>
  );
}

export default Download;
